import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";

const FoldersContext = createContext(null);

export function FoldersProvider({ children }) {
  const [folders, setFolders] = useState([]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/folders");
      setFolders(data);
    } catch {
      setFolders([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Reload folders whenever a non-FoldersProvider creator (e.g. workspace
  // header popover) writes a folder directly.
  useEffect(() => {
    const onChanged = () => load();
    window.addEventListener("crescendo:folders-changed", onChanged);
    return () => window.removeEventListener("crescendo:folders-changed", onChanged);
  }, [load]);

  const create = async (kind, parentFolderIdOrName = null, maybeName = "New folder") => {
    // Iter35: support nested folder creation. Backwards-compatible
    // signature — old callers pass `(kind, name)`, new callers pass
    // `(kind, parent_folder_id, name)`. Detect by sniffing arg shape.
    let parent_folder_id = null;
    let name = "New folder";
    if (typeof parentFolderIdOrName === "string" && parentFolderIdOrName.startsWith("fld_")) {
      parent_folder_id = parentFolderIdOrName;
      if (typeof maybeName === "string" && maybeName) name = maybeName;
    } else if (typeof parentFolderIdOrName === "string") {
      // legacy `(kind, name)`
      name = parentFolderIdOrName;
    }
    const { data } = await api.post("/folders", { kind, name, parent_folder_id });
    setFolders((prev) => [...prev, data]);
    return data;
  };
  const update = async (folder_id, patch) => {
    const { data } = await api.put(`/folders/${folder_id}`, patch);
    setFolders((prev) => prev.map((f) => f.folder_id === folder_id ? data : f));
    return data;
  };
  const remove = async (folder_id) => {
    await api.delete(`/folders/${folder_id}`);
    setFolders((prev) => prev.filter((f) => f.folder_id !== folder_id));
  };

  return (
    <FoldersContext.Provider value={{ folders, create, update, remove, reload: load }}>
      {children}
    </FoldersContext.Provider>
  );
}

export const useFolders = () => useContext(FoldersContext);
